import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth, useAuthState, AUTH_PHASE } from "../contexts/AuthContext";
import { LogOut, Settings as SettingsIcon, Menu, X, BellDot, FileText, Shield, CreditCard, CircleHelp } from "lucide-react";
import { OrgSwitcher } from "./OrgSwitcher";
import { UserAvatar } from "./UserAvatar";
import { AdminConsoleChrome } from "./AdminConsoleChrome";
import { AdminMobileNav } from "./AdminMobileNav";
import { LoadingIndicator } from "./ShellContentFrame";
import { useLayoutMountDebug } from "../hooks/useLayoutMountDebug";
import { LAYOUT_MODE, resolveLayoutMode } from "../lib/layoutRouting";
import { ADMIN_LOGIN, isAuthPending } from "../lib/authRouting";
import { resolveAdminTab } from "../lib/adminNav";
import { api } from "../lib/api";
import { INVITE_INBOX_CHANGED } from "../lib/inviteEvents";
import { LOGO_GREEN } from "../lib/brandColors";
import { BetaBadge } from "./BetaBadge";
import { SiteAnnouncementBar } from "./SiteAnnouncementBar";
import { SiteFooter } from "./SiteFooter";

/** Shared 36×36 header control — approvals bell and profile avatar must match exactly. */
const HEADER_ACTION_CIRCLE =
    "inline-grid size-9 shrink-0 place-items-center overflow-hidden rounded-full border border-white/[0.06] bg-[#262626] p-0 m-0 align-middle leading-none transition-colors hover:border-white/10 hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00BF0D]/35";

function Logo({ compact = false }) {
    return (
        <Link to="/dashboard" className="flex items-center gap-2 fs-pill-anchor" data-testid="logo-home">
            <svg viewBox="0 0 32 32" className="h-5 w-5 shrink-0" aria-hidden>
                <circle cx="16" cy="16" r="13" stroke={LOGO_GREEN} strokeWidth="2" fill="none" />
                <path d="M16 3 A13 13 0 0 1 27.25 22.5 L16 16 Z" fill={LOGO_GREEN} opacity="0.95" />
            </svg>
            {!compact && (
                <span className="font-heading text-base tracking-tight leading-none">
                    <span className="text-white">Fair</span><span style={{ color: LOGO_GREEN }}>Share</span>
                </span>
            )}
            {!compact && <BetaBadge className="hidden sm:inline-flex" />}
        </Link>
    );
}

const NAV_ADMIN = [
    { to: "/dashboard", label: "Dashboard" },
    { to: "/pools", label: "Pools" },
    { to: "/payouts", label: "Payouts" },
    { to: "/approvals", label: "Approvals", badge: "approvals" },
    { to: "/audit", label: "Audit" },
    { to: "/invites", label: "Invites", badge: "invites" },
    { to: "/billing", label: "Billing" },
];
const NAV_MEMBER = [
    { to: "/dashboard", label: "Dashboard" },
    { to: "/invites", label: "Invites", badge: "invites" },
];

function InvitesBadge() {
    const [count, setCount] = useState(0);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const { data } = await api.get("/invitations/inbox/count");
                if (mounted) setCount(data.count || 0);
            } catch (_e) {
                if (mounted) setCount(0);
            }
        };
        load();
        const t = setInterval(load, 20000);
        const onChanged = () => load();
        window.addEventListener(INVITE_INBOX_CHANGED, onChanged);
        return () => { mounted = false; clearInterval(t); window.removeEventListener(INVITE_INBOX_CHANGED, onChanged); };
    }, []);

    if (count <= 0) return null;

    return (
        <span
            className="ml-1.5 fs-pill h-[18px] min-w-[18px] shrink-0 justify-center rounded-full border border-[#00BF0D]/35 bg-[#00BF0D]/10 px-1 text-[10px] font-semibold tabular-nums leading-none text-[#00BF0D]"
            data-testid="invites-pending-badge"
        >
            {count}
        </span>
    );
}

function NavBadge({ type }) {
    if (type === "approvals") return <ApprovalsBadge />;
    if (type === "invites") return <InvitesBadge />;
    return null;
}

function ApprovalsBadge() {
    const { activeOrg } = useAuth();
    const [count, setCount] = useState(0);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            if (!activeOrg?.id) {
                if (mounted) setCount(0);
                return;
            }
            try {
                const { data } = await api.get("/approvals", { params: { org_id: activeOrg.id, status: "pending" } });
                if (mounted) setCount((data.approvals || []).length);
            } catch (_e) {
                if (mounted) setCount(0);
            }
        };
        load();
        const t = setInterval(load, 20000);
        return () => { mounted = false; clearInterval(t); };
    }, [activeOrg?.id]);

    if (count <= 0) {
        return null;
    }

    return (
        <span
            className="ml-1 fs-pill h-[18px] min-w-[18px] shrink-0 justify-center rounded-full bg-[#00BF0D] px-1 text-[10px] font-bold leading-none text-[#0A0A0A]"
            data-testid="approvals-pending-badge"
        >
            {count}
        </span>
    );
}

function AppHeaderBar() {
    const { user, logout, activeOrg } = useAuth();
    const [profileOpen, setProfileOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const profileRef = useRef(null);
    const mobileMenuRef = useRef(null);
    const mobileToggleRef = useRef(null);
    const navigate = useNavigate();
    const NAV = activeOrg?.role === "member" ? NAV_MEMBER : NAV_ADMIN;

    useEffect(() => {
        if (!profileOpen && !mobileOpen) return;
        const onPointerDown = (e) => {
            const inProfile = profileRef.current?.contains(e.target);
            const inMobileMenu = mobileMenuRef.current?.contains(e.target);
            const onMobileToggle = mobileToggleRef.current?.contains(e.target);
            if (profileOpen && !inProfile) {
                setProfileOpen(false);
            }
            if (mobileOpen && !inMobileMenu && !onMobileToggle && !inProfile) {
                setMobileOpen(false);
            }
        };
        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, [profileOpen, mobileOpen]);

    const toggleProfileMenu = () => {
        setMobileOpen(false);
        setProfileOpen((v) => !v);
    };

    const toggleMobileMenu = () => {
        setProfileOpen(false);
        setMobileOpen((v) => !v);
    };

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <div className="app-header-bar relative h-full w-full overflow-visible">
            <nav
                className="pointer-events-none absolute inset-x-0 top-0 z-[5] hidden h-14 items-center justify-center md:flex"
                aria-label="Main navigation"
            >
                <div className="pointer-events-auto flex items-center justify-center gap-0.5">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            className={({ isActive }) =>
                                `inline-flex w-fit shrink-0 items-center rounded-md px-2.5 py-1.5 text-[13px] font-medium transition-colors lg:px-3 ${
                                    isActive ? "bg-white/[0.06] text-white" : "text-white/55 hover:bg-white/[0.03] hover:text-white"
                                }`
                            }
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {item.label}
                            {item.badge && <NavBadge type={item.badge} />}
                        </NavLink>
                    ))}
                </div>
            </nav>

            <div className="relative h-14 w-full overflow-visible">
                <div className="flex h-14 w-full items-center justify-between px-4 sm:px-6 md:px-0">
                    <div className="relative z-10 flex min-w-0 flex-1 items-center gap-1.5 md:absolute md:left-0 md:top-0 md:h-14 md:max-w-[min(42vw,32rem)] md:flex-initial md:pl-6 lg:pl-8">
                        <div className="shrink-0">
                            <Logo />
                        </div>
                        <span className="hidden shrink-0 text-sm text-white/15 sm:inline">/</span>
                        <div className="min-w-0 flex-1">
                            <OrgSwitcher />
                        </div>
                    </div>

                    <div className="relative z-[60] flex shrink-0 items-center gap-2 overflow-visible md:absolute md:right-0 md:top-0 md:h-14 md:items-center md:pr-6 lg:pr-8">
                        <Link
                            to="/faq"
                            className={`${HEADER_ACTION_CIRCLE} inline-grid text-white/65 hover:text-white`}
                            title="Help & FAQ"
                            data-testid="header-faq-icon"
                        >
                            <CircleHelp className="h-4 w-4 shrink-0" />
                        </Link>
                        <Link
                            to="/approvals"
                            className={`${HEADER_ACTION_CIRCLE} ${activeOrg?.role === "member" ? "hidden" : "hidden md:inline-grid"} text-white/65 hover:text-white`}
                            title="Approvals"
                            data-testid="header-approvals-icon"
                        >
                            <BellDot className="h-4 w-4 shrink-0" />
                        </Link>
                        <div className="relative inline-grid shrink-0 place-items-center" ref={profileRef}>
                            <button
                                type="button"
                                onClick={toggleProfileMenu}
                                className={`${HEADER_ACTION_CIRCLE} cursor-pointer text-white`}
                                aria-expanded={profileOpen}
                                aria-haspopup="menu"
                                title="Account menu"
                                data-testid="profile-menu-button"
                            >
                                <UserAvatar
                                    name={user?.name}
                                    avatarUrl={user?.avatar_url}
                                    embedded
                                    className="size-full text-[11px]"
                                />
                            </button>
                            {profileOpen && (
                                <div
                                    className="absolute right-0 top-full z-[80] mt-2 w-56 rounded-xl border border-white/10 bg-[#1A1A1A] p-2 shadow-2xl"
                                    role="menu"
                                    data-testid="profile-menu-dropdown"
                                >
                                    <div className="mb-1 border-b border-white/[0.06] px-3 py-2">
                                        <p className="truncate text-sm font-semibold">{user?.name}</p>
                                        <p className="truncate text-xs text-white/45">{user?.email}</p>
                                    </div>
                                    <button type="button" onClick={() => { setProfileOpen(false); navigate("/faq"); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-white/[0.06]" data-testid="menu-faq">
                                        <CircleHelp className="h-4 w-4" /> Help & FAQ
                                    </button>
                                    <button type="button" onClick={() => { setProfileOpen(false); navigate("/settings"); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-white/[0.06]" data-testid="menu-settings">
                                        <SettingsIcon className="h-4 w-4" /> Settings
                                    </button>
                                    <button type="button" onClick={() => { setProfileOpen(false); navigate("/billing"); }} className={`${activeOrg?.role === "member" ? "hidden" : ""} flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-white/[0.06]`} data-testid="menu-billing">
                                        <CreditCard className="h-4 w-4" /> Billing
                                    </button>
                                    <button type="button" onClick={() => { setProfileOpen(false); navigate("/audit"); }} className={`${activeOrg?.role === "member" ? "hidden" : ""} flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-white/[0.06]`} data-testid="menu-audit">
                                        <FileText className="h-4 w-4" /> Audit log
                                    </button>
                                    <button type="button" onClick={handleLogout} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-white/80 hover:bg-white/[0.06]" data-testid="menu-logout">
                                        <LogOut className="h-4 w-4" /> Sign out
                                    </button>
                                </div>
                            )}
                        </div>
                        <button ref={mobileToggleRef} type="button" className="btn-ghost rounded-md p-2 md:hidden" onClick={toggleMobileMenu} data-testid="mobile-menu-toggle">
                            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                        </button>
                    </div>
                </div>
            </div>
            {mobileOpen && (
                <div ref={mobileMenuRef} className="fixed inset-x-0 top-14 z-50 border-t border-white/[0.06] bg-[#1A1A1A] px-4 py-2 md:hidden shadow-xl">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            onClick={() => setMobileOpen(false)}
                            className={({ isActive }) =>
                                `block rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                                    isActive ? "bg-white/[0.06] text-white" : "text-white/65 hover:bg-white/[0.04]"
                                }`
                            }
                            data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            <span className="inline-flex w-fit shrink-0 items-center">
                                {item.label}
                                {item.badge && <NavBadge type={item.badge} />}
                            </span>
                        </NavLink>
                    ))}
                    <NavLink
                        to="/faq"
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                            `block rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                                isActive ? "bg-white/[0.06] text-white" : "text-white/65 hover:bg-white/[0.04]"
                            }`
                        }
                        data-testid="mobile-nav-help-faq"
                    >
                        Help & FAQ
                    </NavLink>
                </div>
            )}
        </div>
    );
}

function AdminHeaderBar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const currentTab = resolveAdminTab(pathname);

    const handleLogout = async () => {
        await logout();
        navigate(ADMIN_LOGIN, { replace: true });
    };

    return (
        <div className="app-header-bar h-full w-full">
            <div className="mx-auto flex h-14 max-w-7xl items-center gap-2 px-3 sm:gap-3 sm:px-6 lg:px-8">
                <div className="flex min-w-0 shrink items-center gap-2 fs-pill-anchor">
                    <Shield className="admin-accent-icon h-5 w-5 shrink-0" aria-hidden />
                    <span className="font-heading text-base tracking-tight sm:text-lg">
                        <span className="text-white">Fair</span><span className="admin-accent-text">Share</span>
                    </span>
                    <span className="hidden font-sans text-sm text-white/35 sm:inline">Admin</span>
                    <BetaBadge className="hidden sm:inline-flex" />
                    <span className="hidden text-white/20 sm:inline">·</span>
                    <span className="truncate font-sans text-sm text-white/45 lg:hidden">{currentTab.label}</span>
                </div>
                <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
                    <div className="hidden text-right md:block">
                        <p className="max-w-[12rem] truncate text-sm font-medium">{user?.name || "\u00A0"}</p>
                        <p className="max-w-[12rem] truncate text-[11px] text-white/40">{user?.email || "\u00A0"}</p>
                    </div>
                    <UserAvatar name={user?.name} avatarUrl={user?.avatar_url} className="h-9 w-9 text-sm" />
                    <button
                        type="button"
                        onClick={handleLogout}
                        className="btn-ghost inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs sm:px-3"
                        data-testid="admin-logout"
                        aria-label="Sign out"
                    >
                        <LogOut className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Sign out</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Single persistent layout shell for the entire app.
 * Mounts once; only <Outlet /> content changes between auth / app / admin routes.
 */
export function AppLayout() {
    useLayoutMountDebug("AppLayout");
    const { pathname } = useLocation();
    const { authPhase, sessionReady } = useAuthState();
    const mode = resolveLayoutMode(pathname);
    const sessionPending = !sessionReady && authPhase === AUTH_PHASE.CHECKING;
    const announcementAudience = mode === LAYOUT_MODE.ADMIN ? "admin" : mode === LAYOUT_MODE.APP ? "app" : null;

    return (
        <div
            id="app-layout-root"
            className="app-root flex h-full min-h-0 flex-col overflow-hidden bg-[#1E1E1E] text-white"
            data-layout-mode={mode}
            data-testid="app-layout-root"
        >
            {announcementAudience ? <SiteAnnouncementBar audience={announcementAudience} /> : null}
            <header className="app-root__header relative z-40 h-14 shrink-0 overflow-visible border-b border-white/[0.06] bg-[#1A1A1A]/85 backdrop-blur-xl">
                    {mode === LAYOUT_MODE.APP && <AppHeaderBar />}
                    {mode === LAYOUT_MODE.ADMIN && <AdminHeaderBar />}
                </header>

            <main className="app-root__main relative flex min-h-0 flex-1 flex-col overflow-hidden">
                <div className="app-root__viewport relative flex min-h-0 flex-1 flex-col overflow-hidden">
                    {mode === LAYOUT_MODE.ADMIN && (
                        <div className="admin-console-chrome-wrap shrink-0 border-b border-white/[0.06] px-3 py-3 sm:px-6 lg:px-8 lg:py-6">
                            <div className="mx-auto max-w-7xl">
                                <AdminConsoleChrome />
                            </div>
                        </div>
                    )}

                    <div className={`app-root__content relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden ${mode === LAYOUT_MODE.ADMIN ? "admin-root__content" : ""}`}>
                        <div className={mode === LAYOUT_MODE.AUTH ? "h-full min-h-0" : `mx-auto flex w-full min-h-[min(28rem,calc(100vh-3.5rem))] max-w-7xl flex-col px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-10 ${mode === LAYOUT_MODE.ADMIN ? "admin-root__content-inner pb-24 lg:pb-10" : ""}`}>
                            <Outlet />
                            {(mode === LAYOUT_MODE.APP || mode === LAYOUT_MODE.AUTH) && (
                                <SiteFooter variant={mode === LAYOUT_MODE.APP ? "full" : "compact"} className="mt-10 sm:mt-12" />
                            )}
                        </div>
                    </div>

                    {mode === LAYOUT_MODE.ADMIN && <AdminMobileNav />}

                    <div
                        className={`app-root__session-overlay absolute inset-0 z-30 grid place-items-center bg-[#1E1E1E]/90 ${sessionPending ? "" : "pointer-events-none invisible"}`}
                        aria-hidden={!sessionPending}
                        data-testid="session-pending-overlay"
                    >
                        <LoadingIndicator label="Checking session…" testId="session-pending-loading" />
                    </div>
                </div>
            </main>
        </div>
    );
}
